import cx from 'classnames'

import { ReactComponent as CheckIcon } from 'assets/icons/check16.svg'
import Avatar from 'common/Avatar/Avatar'
import {
  FolderBreadcrumb,
  FolderBreadcrumbProps,
  LastFolderBreadcrumb,
} from 'common/Breadcrumbs/components/FolderBreadcrumb/FolderBreadcrumb'
import { OverflowPopover } from 'common/Breadcrumbs/OverflowPopover'
import { buildAncestorProject } from 'common/Breadcrumbs/utils'
import { FolderIcon } from 'common/FolderIcon'
import { Button } from 'components/Button/Button'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useWorkspace } from 'hooks/useWorkspace'
import { AncestorProject, SetState, Workspace } from 'types'
import { getInitials } from 'utils'
import { NO_FOLDER_TITLE } from 'utils/constants'

import Styles from './Breadcrumb.module.scss'

interface FolderBreadcrumbWithAncestors extends FolderBreadcrumbProps {
  ancestor_projects: AncestorProject[]
}

interface BreadcrumbProps {
  topLevelFolder: AncestorProject | null
  selectedFolder: AncestorProject | null
  setChangedWorkspace: SetState<boolean>
  setPageLoaded?: SetState<boolean>
  setSelectedFolder: (folder: AncestorProject | null) => void
  setTopLevelFolder: SetState<AncestorProject | null>
  setWorkspaceId: SetState<number>
  workspaceId: number
  disabled?: boolean
  workspaces?: Workspace[]
}

export const Breadcrumb = ({
  workspaceId,
  workspaces = [],
  selectedFolder,
  setSelectedFolder,
  topLevelFolder,
  setPageLoaded,
  setWorkspaceId,
  setChangedWorkspace,
  setTopLevelFolder,
  disabled,
}: BreadcrumbProps) => {
  const workspace = useWorkspace(workspaceId)
  const { isDesktop, isMobile } = useResponsiveDesign()
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  const MAX_BREADCRUMBS_TO_SHOW = isMobile ? 0 : isDesktop ? 2 : 1

  const onWorkspaceClick = (id: number) => {
    setWorkspaceId(id)
    setSelectedFolder(null)
    setChangedWorkspace(true)
  }

  const resetFolders = () => {
    setSelectedFolder(null)
    setTopLevelFolder(null)
  }

  const resetToProject = (project: AncestorProject) => {
    setSelectedFolder(project)
    setTopLevelFolder(project)
    setPageLoaded?.(false)
  }

  if (!workspace) return null

  const getAncestors = () => {
    if (!!selectedFolder && selectedFolder.ancestor_projects) {
      return selectedFolder.ancestor_projects
    } else if (!!topLevelFolder && !selectedFolder) {
      return topLevelFolder.ancestor_projects
    }
    return []
  }

  let ancestors = getAncestors()
  const totalBreadCrumbs = ancestors?.length + 1
  const isPopoverNeeded = totalBreadCrumbs > 1 && totalBreadCrumbs > MAX_BREADCRUMBS_TO_SHOW
  let breadcrumbNodes: FolderBreadcrumbWithAncestors[] = []
  let nodesToShowInPopover: FolderBreadcrumbWithAncestors[] = []
  const ancestorLinks: FolderBreadcrumbWithAncestors[] = isPopoverNeeded
    ? [
        {
          icon: <FolderIcon defaultFolder={false} header_emoji={null} />,
          defaultFolder: false,
          title: NO_FOLDER_TITLE,
          onClick: resetFolders,
          ancestor_projects: [],
          to: '',
        },
      ]
    : []

  ancestors?.forEach((ancestor) => {
    ancestorLinks.push(buildAncestorProject(ancestor, () => resetToProject(ancestor)))
  })
  if (isPopoverNeeded) {
    ancestorLinks.forEach((ancestor, index) => {
      if (index < totalBreadCrumbs - MAX_BREADCRUMBS_TO_SHOW) {
        nodesToShowInPopover.push(ancestor)
      } else {
        breadcrumbNodes.push(ancestor)
      }
    })
  } else {
    breadcrumbNodes = ancestorLinks
  }

  return (
    <div className={cx(Styles.breadcrumb, Styles.v2, { [Styles.disabledBreadCrumb]: disabled })}>
      <DropdownTrigger placement="bottom-start">
        <Button
          size="md"
          variant="tertiary"
          className={cx(Styles.workspaceButton, Styles.v2)}
          data-testid="workspaceSelectorTrigger"
        >
          <Avatar label={getInitials(workspace.name)} size={20} url={workspace.logo_url} workspaceId={workspace.id} />
        </Button>
        {(close) => (
          <div className={Styles.workspaceMenu}>
            {workspaces.map(({ id, name, logo_url }) => {
              return (
                <div
                  key={id}
                  onClick={() => {
                    onWorkspaceClick(id)
                    close()
                  }}
                  className={Styles.workspaceRow}
                >
                  <div className={Styles.workspaceName}>
                    <Avatar label={getInitials(name)} size={24} url={logo_url} />
                    <span className={Styles.text}>{name}</span>
                  </div>
                  {workspaceId === id && <CheckIcon />}
                </div>
              )
            })}
          </div>
        )}
      </DropdownTrigger>

      {!isPopoverNeeded &&
        (!!selectedFolder || !!topLevelFolder ? (
          <>
            <div className={Styles.divider}>/</div>
            <Button
              size="md"
              variant="tertiary"
              isDisabled={disabled}
              onPress={resetFolders}
              className={cx(Styles.folderButton, Styles.noIconButton)}
            >
              {NO_FOLDER_TITLE}
            </Button>
          </>
        ) : (
          <>
            <div className={Styles.divider}>/</div>
            <div className={Styles.currentFolder}>{NO_FOLDER_TITLE}</div>
          </>
        ))}

      {isPopoverNeeded && <OverflowPopover popoverItems={nodesToShowInPopover.reverse()} />}

      {!!selectedFolder &&
        breadcrumbNodes.map((project: FolderBreadcrumbWithAncestors, index) => (
          <>
            <div className={Styles.divider}>/</div>
            <FolderBreadcrumb
              key={`project-${index}`}
              title={project.title}
              onClick={project.onClick}
              icon={project.icon}
              defaultFolder={project.defaultFolder}
              to={project.to}
            />
          </>
        ))}
      {!!selectedFolder && (
        <LastFolderBreadcrumb
          title={selectedFolder.title}
          showDivider
          icon={
            <FolderIcon defaultFolder={!!selectedFolder.default_project} header_emoji={selectedFolder.header_emoji} />
          }
        />
      )}
    </div>
  )
}
